import React, { useCallback, useContext, useEffect, useMemo } from 'react';
import type { ColDef } from 'ag-grid-community';

import DataTable from 'components/DataTable';
import DataTableRenderers from 'components/DataTable/DataTable.renderers';
import { ShimSortModel } from 'components/DataTable/agParamsShim';
import buildGridOptions from 'components/Grid/buildGridOptions';
import HideUnlessMouseOver from 'components/HideUnlessMouseOver';
import IncompatibleComparison from 'components/IncompatibleComparison';
import { CardSkeleton } from 'components/Skeleton';
import { RowHeightContext } from 'contextProviders/SplashScreenProviders/MiscProviders/RowHeightProvider';
import { getSeriesDataKey } from 'components/V5Gadget/seriesDataKey';
import GridRowCountContext from 'contexts/GridRowCountContext';
import MetricMatrixContext from 'contexts/MetricMatrixContext';
import MetricOptionsContext from 'contexts/MetricOptionsContext';
import ReportContext from 'contexts/ReportContext';
import { useRowHighlightPredicate } from 'hooks/useRowHighlighter';

import stackSecondaryRows, { getSecondarySubRows } from './stackSecondaryRows';
import useApplyMatrixSortChange from './useApplyMatrixSortChange';

// The V5 Matrix drawn by DataTable (dataTable gate). Same colDefs,
// rows, totals and renderers as the AG path; they arrive as props, so there is
// no imperative row pushing, refreshCells or remounting.
const MatrixDataTableView = ({
  chartDefinition,
  isCompatibleComparison,
  columnDefs,
  rowsToDisplay,
  totals,
  gridId,
  onDrillDown,
  isLoading,
  isInitialLoading,
  noDataToDisplay,
  autoSizeFirstColumnOnly,
  additionalAutoSizeColumnIds,
  drillField,
  setExportHandle,
  isSecondaryRowsStacked,
  secondaryRowParents,
}: {
  chartDefinition: V5ChartDefinition;
  isCompatibleComparison: boolean | undefined;
  columnDefs: ColDef[];
  rowsToDisplay: DataTable.Row[];
  totals: DataTable.Row[];
  gridId: string;
  onDrillDown: (term: string, ctrlKey?: boolean) => void;
  isLoading: boolean;
  isInitialLoading: boolean;
  noDataToDisplay: boolean;
  autoSizeFirstColumnOnly: boolean;
  additionalAutoSizeColumnIds?: string[];
  drillField: string;
  setExportHandle: (handle: DataTable.ExportHandle | undefined) => void;
  // Each dimension A value a collapsible row of its own (its subtotals:
  // secondaryRowParents), its secondary rows under it
  isSecondaryRowsStacked?: boolean;
  secondaryRowParents?: DataTable.Row[];
}) => {
  const { rowHeight } = useContext(RowHeightContext);
  const { isPdfExport } = useContext(ReportContext);
  const applyMatrixSortChange = useApplyMatrixSortChange(chartDefinition);
  const isRowHighlighted = useRowHighlightPredicate({ drillField });

  const defaultColDef = useMemo(
    () =>
      buildGridOptions({ rowModelType: 'clientSide', rowHeight }).defaultColDef,
    [rowHeight],
  );
  const context = useMemo(() => ({ onDrillDown }), [onDrillDown]);

  const onSortChanged = useCallback(
    (sortModel: ShimSortModel) => {
      if (sortModel.length > 0) {
        applyMatrixSortChange(sortModel[0]);
      }
    },
    [applyMatrixSortChange],
  );
  const isRowSelected = useCallback(
    (data: DataTable.Row) => isRowHighlighted(data) === true,
    [isRowHighlighted],
  );

  const field = chartDefinition.dimensionA?.field;
  const secondaryField = chartDefinition.secondaryDimensionA?.field;
  const isStacked = !!isSecondaryRowsStacked && !!field && !!secondaryField;
  // Columns of each row's latest value (left-aligned, see
  // shouldLeftAlignMetric), which a stacked parent counts instead
  const { metricOptionsLookup } = useContext(MetricOptionsContext);
  const distinctCountColumns = useMemo(
    () =>
      chartDefinition.series
        .filter((s) => {
          const metric = metricOptionsLookup[s.metricId];
          return (
            !!metric &&
            metric.type === 'normal' &&
            (metric.aggFunc === 'first' || metric.aggFunc === 'last')
          );
        })
        .map(getSeriesDataKey),
    [chartDefinition.series, metricOptionsLookup],
  );
  const rows = useMemo(
    () =>
      isStacked && field && secondaryField
        ? stackSecondaryRows({
            parentRows: secondaryRowParents ?? [],
            rows: rowsToDisplay,
            field,
            secondaryField,
            distinctCountColumns,
          })
        : rowsToDisplay,
    [
      distinctCountColumns,
      field,
      isStacked,
      rowsToDisplay,
      secondaryField,
      secondaryRowParents,
    ],
  );

  // Bring a drilled-into row into view, as the AG path does on rowSelected
  useEffect(() => {
    const selected = document
      .getElementById(gridId)
      ?.querySelector('tr.dt-selected');
    if (selected && typeof selected.scrollIntoView === 'function') {
      selected.scrollIntoView({ block: 'nearest' });
    }
  }, [gridId, rows, isRowHighlighted]);

  // One value per onDrillDown: a new object each render would re-render
  // every cell that reads it (as the matrix's card resizes)
  const metricMatrixContextValue = useMemo(
    () => ({ onDrillDown }),
    [onDrillDown],
  );

  const gridRowCountContextValue = useMemo(
    () => ({ rowCount: rowsToDisplay.length }),
    [rowsToDisplay.length],
  );

  if (!isCompatibleComparison) {
    return <IncompatibleComparison />;
  }

  if (isInitialLoading) {
    return <CardSkeleton />;
  }

  return (
    <MetricMatrixContext.Provider value={metricMatrixContextValue}>
      <GridRowCountContext.Provider value={gridRowCountContextValue}>
        <HideUnlessMouseOver>
          <DataTable
            tableId={gridId}
            testId="grid"
            columnDefs={columnDefs}
            defaultColDef={defaultColDef}
            components={DataTableRenderers}
            rows={rows}
            totalsRows={totals}
            context={context}
            sizing={
              chartDefinition.gridSizing === 'fitContent'
                ? 'fitContent'
                : 'fitWidth'
            }
            isFirstColumnFitted={autoSizeFirstColumnOnly}
            // As the AG matrix's autoSizeAllColumns(skipHeader)
            skipHeaderWhenSizing
            fittedColumnIds={additionalAutoSizeColumnIds}
            onSortChanged={onSortChanged}
            isRowSelected={isRowSelected}
            getSubRows={isStacked ? getSecondarySubRows : undefined}
            // Nothing can be clicked open in the scheduled-report PDF
            isExpandedByDefault={isStacked && !!isPdfExport}
            isParentRowExported={isStacked}
            isLoading={isLoading}
            noDataToDisplay={noDataToDisplay}
            isHeadlessHeightEnabled
            onExportHandle={setExportHandle}
          />
        </HideUnlessMouseOver>
      </GridRowCountContext.Provider>
    </MetricMatrixContext.Provider>
  );
};

export default MatrixDataTableView;
